/**
 * useGeolocationPermission (#8 Phase 3g) — reactive geolocation permission
 * state for the /tracking BottomNav tab gating.
 *
 * The Permissions API exposes 'prompt' | 'granted' | 'denied' for geolocation,
 * with a `change` event when the user updates the browser setting. Older browsers
 * (notably pre-16.4 Safari) lack the API entirely — we treat that as
 * 'unsupported' and the caller decides whether to show the surface (we currently
 * do, so the geolocation request can fail naturally and the user discovers it).
 *
 * D3's visibility rule is GONE (#449): the tab and the route no longer
 * depend on this state at all — only on the feature flag. What this state
 * still decides is whether the tracking LOOP arms the browser's receiver,
 * and which placeholder the view shows. Historical D3 rule, for context:
 * tab was visible on 'prompt' | 'granted' | 'unsupported', hidden on
 * 'denied'. The composable is a singleton — one query, one listener, shared
 * across all callers.
 *
 * Two extra invariants beyond D3 (added after PR #46 review):
 *   F5 (HISTORICAL — removed by #449): `settled` flag. BottomNav used to wait
 *       for the initial query before deciding tab visibility, so a
 *       previously-denied user never saw a flash of the tab. The tab no
 *       longer depends on this state at all. The flag itself stays: the
 *       /tracking route guard waits on it (bounded) so App.vue's immediate
 *       watcher does not arm watchPosition against the default 'prompt'.
 *   F3: `setRuntimePermissionDenied()` — bridges runtime PERMISSION_DENIED from
 *       watchPosition back into the singleton (some browsers' 'change' event is
 *       delayed/missing), preventing the re-prompt loop on every tab tap.
 */
import { readonly, ref } from 'vue';

export type GeolocationPermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported';

const _state = ref<GeolocationPermissionState>('prompt');
// Settled = the initial query landed (or determined unsupported). BottomNav
// gates on this so previously-denied users don't see a flash of the tab
// during the async query window.
const _settled = ref(false);
let _initStarted = false;
// Resolvers waiting on the initial query to settle (PR #46 review F1 — the
// router's beforeEnter guard awaits this so it doesn't race the async
// Permissions API).
let _settledResolvers: Array<() => void> = [];

function _markSettled(): void {
  _settled.value = true;
  const pending = _settledResolvers;
  _settledResolvers = [];
  for (const r of pending) r();
}

async function _init(): Promise<void> {
  if (_initStarted) return;
  _initStarted = true;

  if (!('permissions' in navigator)) {
    _state.value = 'unsupported';
    _markSettled();
    return;
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    _state.value = status.state as GeolocationPermissionState;
    _markSettled();
    // Listener attached for the app's lifetime — singleton, no ref-counting.
    status.addEventListener('change', () => {
      _state.value = status.state as GeolocationPermissionState;
    });
  } catch {
    // F9: a transient failure here previously latched 'unsupported' for the
    // whole session because _initialized was set BEFORE the await. Now
    // _initStarted is reset so the next caller retries.
    _initStarted = false;
    // Until a retry succeeds, treat as unsupported so callers can render
    // a stable state.
    _state.value = 'unsupported';
    _markSettled();
  }
}

/**
 * Resolves once the initial Permissions API query has landed (or determined
 * unsupported). Used by router beforeEnter guards to avoid racing the async
 * query on cold-start deep links (PR #46 review F1).
 */
export function waitForPermissionSettled(): Promise<void> {
  if (_settled.value) return Promise.resolve();
  void _init();
  return new Promise((resolve) => _settledResolvers.push(resolve));
}

/**
 * Bridge runtime PERMISSION_DENIED from navigator.geolocation.watchPosition
 * back into the singleton (PR #46 review F3). The Permissions API 'change'
 * event is sometimes delayed or missing (notably some Android Chromes); this
 * keeps the two classifiers in sync so the view switches to the denied
 * placeholder immediately (it no longer hides the tab — #449)
 * and the App.vue auto-start gate stops re-firing.
 */
export function setRuntimePermissionDenied(): void {
  _state.value = 'denied';
  _markSettled();
}

/**
 * #527 — ask the Permissions API again. The runtime latch above also fires
 * when the pilot merely DISMISSES the native dialog (Chrome's X), which the
 * API still reports as 'prompt'; without this, "Use my location" would be
 * dead for the session and the banner would send them to OS settings that
 * are not wrong. No Permissions API: nothing to ask, the state stands.
 */
export async function requeryPermission(): Promise<void> {
  if (!('permissions' in navigator)) {
    // Nothing to ask. The only way this singleton reads 'denied' here is
    // the runtime latch, which a dismissed dialog also sets — lift it, so
    // the receiver may ask again; a real refusal comes straight back.
    _state.value = 'unsupported';
    return;
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    _state.value = status.state as GeolocationPermissionState;
  } catch {
    // Keep whatever we had; the caller proceeds on it.
  }
}

export function useGeolocationPermission() {
  // Fire-and-forget — the ref is reactive, callers can read it before the
  // promise resolves (it'll just start at 'prompt' / settled=false until
  // the query lands).
  void _init();
  return {
    permissionState: readonly(_state),
    permissionSettled: readonly(_settled),
  };
}

// Kick off the query at module import so the singleton is as warm as possible
// by the time BottomNav / App.vue read it during their setup.
void _init();
